import React from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CollapseIcon from "@mui/icons-material/CloseFullscreen";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import RichTextEditor from "../editors/RichTextEditor";
import FilePreview from "./FilePreview";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import { composerHasTextPayload } from "./composerHtmlUtils";

const InternalNoteExpandedEditor = ({
  open,
  onClose,
  message,
  setMessage,
  onSend,
  openFilePicker,
  file,
  files = [],
  onRemoveFile,
  audioURL = null,
  title,
  hint,
  sendLabel,
  cancelLabel,
  closeAriaLabel,
  showAttachButton = true,
}) => {
  const { t } = useTranslation("chat");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const attachmentList = files.length ? files : file ? [file] : [];
  const canSend =
    composerHasTextPayload(message) ||
    composerHasSendPayload("", file, files, audioURL);

  const handleSend = async () => {
    if (!canSend || typeof onSend !== "function") return;
    try {
      await onSend();
      onClose();
    } catch (_) {
      // O chamador exibe o erro; mantém o editor aberto para não perder o texto.
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "94%" : "min(720px, 88vw)",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.15rem" } }}>
            {title ?? t("compose.internalEditorTitle", "Anotação interna")}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label={closeAriaLabel ?? t("compose.internalEditorClose", "Fechar")}
          >
            <CollapseIcon />
          </IconButton>
        </Box>

        {hint ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.45 }}>
            {hint}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.45 }}>
            {t("compose.internalEditorHint", "Esta mensagem não será enviada ao cliente.")}
          </Typography>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: isMobile ? 220 : 280,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <RichTextEditor
            value={message}
            onChange={setMessage}
            height={isMobile ? 220 : 280}
            stickyToolbar
          />
        </Box>

        {attachmentList.length > 0 && (
          <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {attachmentList.map((f, index) => (
              <FilePreview
                key={`${f?.name}-${f?.size}-${index}`}
                file={f}
                onRemove={() => onRemoveFile?.(index)}
              />
            ))}
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mt={2}>
          {showAttachButton ? (
            <IconButton onClick={openFilePicker} aria-label={t("input.menuAttachAria", "Anexar arquivo")}>
              <AttachFileIcon />
            </IconButton>
          ) : null}
          <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none" }}>
            {cancelLabel ?? t("compose.internalEditorCancel", "Cancelar")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSend}
            disabled={!canSend}
            sx={{ textTransform: "none" }}
          >
            {sendLabel ?? t("compose.internalEditorSend", "Registrar anotação")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default InternalNoteExpandedEditor;
